import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ClickAwayListener, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Appointment, { status as AppointmentStatusIds } from '@spike/appointment-model';
import clsx from 'clsx';
import { useApiClientWrapper, useMasterData } from 'hooks';
import { FunctionComponent, MouseEventHandler, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PulseLoader from 'react-spinners/PulseLoader';
import List from './List';
import {
    AppointmentsStatus,
    cancelAppointmentThunk,
    changeStatusAppointmentThunk,
    completeAppointmentThunk
} from '@spike/bookings-action';
import { RootState } from 'store';
import { OrdersStatus, reloadOrderThunk } from '@spike/orders-action';
import useNonInitialEffect from '@versiondos/hooks';
import PopUpCancelBooking, { reasonNoShowId } from 'components/UI/PopUpCancelBooking';
import { NO_SHOW_CANCEL_REASON_ID } from 'model';
import Booking from '@spike/booking-model';

export interface BookingStatusButtonProps {
    loading?: boolean;
    disabled?: boolean;
    className?: string;
    onConfirm: () => void;
    onCancel: () => void;
}

interface ButtonColors {
    color: string;
    backgroundColor: string;
    borderColor: string;
}

const bookedColor: ButtonColors = { color: '#EAB464', backgroundColor: '#FFF', borderColor: '#EAB464' };

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            maxWidth: 130
        },
        button: (buttonColor: ButtonColors) => ({
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
            alignItems: 'center',
            padding: '9px 16px',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: buttonColor.borderColor,
            borderRadius: 50,
            color: buttonColor.color,
            background: buttonColor.backgroundColor
        }),
        buttonLabel: {
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '100%',
            textAlign: 'center',
            width: '100%'
        },
        chevronIcon: {
            marginLeft: 10
        },
        spinnerContainer: (buttonColor: ButtonColors) => ({
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            paddingBottom: 5,
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: buttonColor.borderColor,
            borderRadius: 50,
            color: buttonColor.color,
            background: buttonColor.backgroundColor
        })
    })
);

export const BookingStatusButton: FunctionComponent<BookingStatusButtonProps> = props => {
    const color = bookedColor;

    const classes = useStyles(color);

    const masterData = useMasterData();

    const [expanded, setExpanded] = useState(false);

    const [loading, setLoading] = useState(false);

    const actualStatus = masterData.workflow.find(step => step.status.id === AppointmentStatusIds.BOOKED);

    const actions =
        actualStatus?.actions.filter(
            action =>
                action.show &&
                [AppointmentStatusIds.CONFIRMED, AppointmentStatusIds.CANCELLED].includes(action.nextStatus.id)
        ) ?? [];

    const hasActions = actions.length > 0;

    const buttonLabel = actualStatus?.status.name ?? '-';

    useNonInitialEffect(() => {
        props.loading !== loading && setLoading(props.loading ?? false);
    }, [props.loading]);

    const changeStatus = (statusId: number) => {
        if (statusId === AppointmentStatusIds.CANCELLED) {
            props.onCancel();
        } else if (statusId === AppointmentStatusIds.CONFIRMED) {
            setLoading(true);
            props.onConfirm();
        }
    };

    const clickHandler = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        !loading && !props.disabled && setExpanded(prev => !prev);
    };

    const selectHandler = (statusId: number) => {
        setExpanded(false);
        changeStatus(statusId);
    };

    const spinner = (
        <Box className={classes.spinnerContainer}>
            <PulseLoader
                color={color.color}
                size={5}
            />
        </Box>
    );

    return (
        <>
            <ClickAwayListener onClickAway={() => setExpanded(false)}>
                <Box className={clsx(classes.container, props.className)}>
                    {loading && spinner}
                    <Box
                        className={classes.button}
                        onClick={clickHandler}
                    >
                        <Typography className={classes.buttonLabel}>{buttonLabel}</Typography>
                        {hasActions && (
                            <FontAwesomeIcon
                                icon={expanded ? faChevronUp : faChevronDown}
                                className={classes.chevronIcon}
                            />
                        )}
                    </Box>
                    {expanded && hasActions && !loading && actualStatus && (
                        <List
                            actions={actions}
                            onClick={selectHandler}
                            onClose={() => setExpanded(false)}
                        />
                    )}
                </Box>
            </ClickAwayListener>
        </>
    );
};

export default BookingStatusButton;
